import React from 'react';
import { Button, Dialog, Dropdown, Label } from 'components';
import { Callout } from '@blueprintjs/core';
import { LINES_QUERY, TRANSFER_USER_TO_LINE_MUTATION } from 'gql';
import { useQuery, useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import { TRANSFER_USER_TO_LINE_VALIDATION } from 'validations';
import { useModal } from 'providers/modalProvider';
import { useToast } from 'providers/toastProvider';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  find,
  map,
  pipe,
  pluck,
  prop,
  propEq,
  reject,
  toLower,
  toUpper,
  uniq,
} from 'ramda';
import { SPORTS } from 'utils/enums';
import {
  getFunctions,
  getJobTitles,
  getLinesBySport,
  optionsFromArr,
} from 'utils/helpers';

const TransferUserToLine = () => {
  const { formatMessage } = useIntl();
  const { modalDispatch, modalState } = useModal();
  const { showToast } = useToast();
  const { user = {}, refetch } = modalState;
  const { id: userId, firstName, middleName, lastName } = user;
  const sourceLineId = user?.jobTitleRole?.role?.line?.id;
  const fullName = `${firstName || ''} ${lastName || ''}`.trim();
  const headerName = `${firstName || ''} ${middleName || ''} ${lastName || ''}`
    .replace(/\s+/g, ' ')
    .trim();

  const closeModal = () => modalDispatch({ type: 'ClOSE_ALL' });

  const { data: { lines = [] } = {}, loading: linesLoading } =
    useQuery(LINES_QUERY);

  const [transferMutation, { loading: transferLoading }] = useMutation(
    TRANSFER_USER_TO_LINE_MUTATION,
    {
      // Keep the dialog open on error so the admin can adjust and retry; the
      // toast carries the server's (already user-facing) message.
      onError: (err) => {
        const message = (
          err.graphQLErrors?.[0]?.message || err.message
        ).replace(/^GraphQL error:\s*/, '');
        showToast({ message, icon: 'error' });
      },
      onCompleted: () => {
        closeModal();
        showToast({
          message: formatMessage({
            id: 'containers.users.modals.transfer.success',
          }),
        });
        refetch?.();
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      sport: '',
      lineId: '',
      jobTitleId: '',
      lineFunctionId: undefined,
    },
    validationSchema: TRANSFER_USER_TO_LINE_VALIDATION,
    // Move validates the selection, then fires the mutation directly.
    onSubmit: (values) =>
      transferMutation({
        variables: {
          input: {
            userId,
            targetJobTitleId: values.jobTitleId,
            targetLineFunctionId: values.lineFunctionId || undefined,
          },
        },
      }),
  });

  // The chosen line carries its own sport, so job-title / function lookups work
  // whether or not the optional Sport filter is set.
  const selectedLine = find(propEq('id', formik.values.lineId), lines);
  const targetLineSport = selectedLine?.sport;

  const sportsList = pipe(pluck('sport'), uniq)(lines);
  const sportOptions = optionsFromArr(
    map((sport) => prop(toLower(sport), SPORTS), sportsList)
  );

  // Optional Sport narrows the line list; always exclude the user's own line.
  const targetLineItems = formik.values.sport
    ? getLinesBySport(lines, formik.values.sport)
    : lines;
  const targetLineOptions = optionsFromArr(
    reject(propEq('id', sourceLineId), targetLineItems)
  );
  const jobTitleOptions = optionsFromArr(
    getJobTitles(lines, targetLineSport, formik.values.lineId)
  );
  const lineFunctionOptions = optionsFromArr(
    getFunctions(lines, targetLineSport, formik.values.lineId)
  );

  return (
    <Dialog
      dialogTitle={
        <FormattedMessage id="containers.users.modals.transfer.title" />
      }
      className="transfer-dialog"
      width="540px"
      icon="follower"
    >
      <div className="bp3-dialog-body" data-testid="transfer-body">
        <div data-testid="transfer-form">
          <Callout
            className="transfer-help"
            intent="primary"
            icon="info-sign"
            data-testid="transfer-help"
          >
            <FormattedMessage id="containers.users.modals.transfer.help" />
          </Callout>
          <Label
            variant="AvatarLabel"
            labelText={headerName}
            avatarText={fullName}
            className="mgb-15"
          />
          <div className="transfer-row">
            <Dropdown
              items={sportOptions}
              value={prop(toLower(formik.values.sport), SPORTS) || ''}
              onChange={({ value }) =>
                formik.setValues({
                  ...formik.values,
                  sport: toUpper(value),
                  lineId: '',
                  jobTitleId: '',
                  lineFunctionId: undefined,
                })
              }
              buttonText={formatMessage({
                id: 'containers.users.header.sport',
              })}
              name="sports"
              data-testid="transfer-sport"
              loading={linesLoading}
              clearable
              onClear={() =>
                formik.setValues({
                  ...formik.values,
                  sport: '',
                  lineId: '',
                  jobTitleId: '',
                  lineFunctionId: undefined,
                })
              }
            />
            <Dropdown
              items={targetLineOptions}
              value={formik.values.lineId || ''}
              onChange={({ value }) =>
                formik.setValues({
                  ...formik.values,
                  lineId: value,
                  jobTitleId: '',
                  lineFunctionId: undefined,
                })
              }
              error={formik.touched.lineId && formik.errors.lineId}
              buttonText={formatMessage({
                id: 'containers.users.form.line',
              })}
              name="transfer-line"
              data-testid="transfer-line"
              loading={linesLoading}
              disabled={!formik.values.sport}
              disableDropdownButton={linesLoading || !formik.values.sport}
            />
          </div>
          <div className="transfer-row">
            <Dropdown
              items={jobTitleOptions}
              value={formik.values.jobTitleId || ''}
              onChange={({ value }) =>
                formik.setFieldValue('jobTitleId', value)
              }
              error={formik.touched.jobTitleId && formik.errors.jobTitleId}
              buttonText={formatMessage({
                id: 'containers.users.jobTitle',
              })}
              name="transfer-jobTitle"
              data-testid="transfer-jobTitle"
              loading={linesLoading}
              disableDropdownButton={!formik.values.lineId}
            />
            <Dropdown
              items={lineFunctionOptions}
              value={formik.values.lineFunctionId || ''}
              onChange={({ value }) =>
                formik.setFieldValue('lineFunctionId', value)
              }
              buttonText={formatMessage({
                id: 'containers.users.form.lineFunction',
              })}
              name="transfer-lineFunction"
              data-testid="transfer-lineFunction"
              loading={linesLoading}
              disableDropdownButton={!formik.values.lineId}
              clearable
              onClear={() => formik.setFieldValue('lineFunctionId', undefined)}
            />
          </div>
        </div>
      </div>
      <div className="bp3-dialog-footer" data-testid="transfer-footer">
        <div className="bp3-dialog-footer-actions">
          <Button
            outlined
            intent="Primary"
            text={formatMessage({ id: 'containers.all.btn.cancel' })}
            onClick={closeModal}
            data-testid="transfer-cancel"
          />
          <Button
            intent="Primary"
            text={formatMessage({
              id: 'containers.users.modals.transfer.submit',
            })}
            onClick={formik.submitForm}
            loading={transferLoading}
            data-testid="transfer-submit"
          />
        </div>
      </div>
    </Dialog>
  );
};

export default TransferUserToLine;
